/**
 * Our own pruned view of Ralawise's "Categorisation" tags.
 *
 * The feed attaches dozens of tags per style, mixing real taxonomy with
 * campaigns, directories and promotions. This file is the single place that
 * decides what the catalogue shows:
 *
 *   DROP      patterns for marketing noise, removed entirely
 *   RENAME    feed wording → our wording (also collapses variants)
 *   SECTOR    tags that describe who/what the garment is for
 *   ATTRIBUTE tags that describe a property of the garment
 *   everything else that survives is a CATEGORY (garment family)
 *
 * Edit the lists, nothing else needs to change. Wording is a first pass for
 * the client to check against the categories spreadsheet.
 */

const DROP: RegExp[] = [
  /directory/i,
  /raladeal/i,
  /ralateam/i,
  /ralawise/i,
  /^eve - /i,
  /^cathryn - /i,
  /visionnaire/i,
  /^p&p /i,
  /^ire /i,
  /must haves?$/i,
  /^new sectors? - /i,
  /^new sub-sectors? - /i,
  /^new (in|colours?|sizes?|styles?)\b/i,
  /just arrived/i,
  /latest additions/i,
  /promotion/i,
  /campaign/i,
  /exclusives?$/i,
  /gifting guide/i,
  /essentials?$/i,
  /essential styles/i,
  /shades of the season/i,
  /important info/i,
  /prices? collection/i,
  /back to education/i,
  /bon voyage/i,
  /beat the chill/i,
  /washed & dyed collection/i,
  /the heavyweight collection/i,
  /the upf collection/i,
  /tech bags collection/i,
  /corporate collection$/i,
  /polos - that do more/i,
  /^tees & polos - (full collection|sport|corporate|hospitality|trade|long sleeve)$/i,
  /^hoodies & sweats - (zipped|premium)$/i,
  /^tees - (midweight|lightweight|heavyweight|long sleeve)/i,
  /^zipped styles/i,
  /^outerwear (padded - thin baffles|corporate layers|- 3 layer softshells)$/i,
  /^fleece - (sustainable|microfleece)$/i,
  /^workwear - performance jackets$/i,
  /^bags & luggage ralateam$/i,
  /eu exclusives/i,
  /print and embroidery supplies/i,
  // supplier/brand curated collections and seasonal edits
  /^edge\b/i,
  /^cathr/i,
  /^eve\b/i,
  /^the upf/i,
  /sale\b/i,
  /^test\b/i,
  /^henbury/i,
  /^premier /i,
  /^selected /i,
  /^stanley\/ ?stella$/i,
  /^tee jays/i,
  /^r star /i,
  /top \d+ sellers/i,
  /^back to /i,
  /^sale$/i,
  /10% off/i,
  /label free/i,
  /perfect for dtg/i,
  /christmas|festive|freshers|spring styles|summer styles|summer$|winter warmers|trending|on-trend|hats on trend|home of the hoodie|days out|stay active|lightweight layers|conscious cold weather|pastels|hyperbrights|home comforts|street casual|outdoor dining|alfresco dining|shades of/i,
  // sub-variants of a family we already keep as one category
  /^outerwear .+/i,
  /^workwear - /i,
  /^hoodies & sweats - /i,
  /^gilets - /i,
  /^fleece - /i,
  /^tees - /i,
  /^polos - /i,
  /polycotton polos/i,
  /poplin shirts/i,
  // price and brand promotions, internal markers, fragments
  /^pru - /i,
  /request a sample/i,
  /pric(e|ing)/i,
  /promo/i,
  /reduction/i,
  /\boff\b/i,
  /^test$/i,
  /brand of the month/i,
  /trend colours/i,
  /coastal collection/i,
  /cabin friendly/i,
  /next gen/i,
  /heiq/i,
  /^new\b/i,
  /^ral/i,
  /^eve?$/i,
  /^sus/i,
  /^ev$/i,
  /-20\d\d$/i,
  /gildan|awdis|fotl|under armour|kustom kit|bella\+canvas|craghoppers|just cool|anthem|mumbles|b&c /i,
  /embroidery$/i,
  /face covers/i,
  /^premium$/i,
  /^s\/s \d\d/i,
];

const RENAME: Record<string, string> = {
  'Outerwear Full Collection': 'Outerwear',
  'Tees & Polos - Full Collection': 'Tees & Polos',
  'Hoodies & Sweats - Full Collection': 'Hoodies & Sweats',
  'Gilets and Bodywarmers': 'Gilets & Bodywarmers',
  'Sustainable (Directory Tick) All 2026': 'Sustainable',
  'Sustainable (Directory Tick) New Recycled 2026': 'Recycled',
  'Sustainable (Directory Tick) New Organic 2026': 'Organic',
  'Safe to wash at 60 degrees': 'Wash at 60°',
  'UPF Protection': 'UPF protection',
  'Outerwear Padded': 'Padded',
  'Outerwear Rainwear': 'Rainwear',
  'Outerwear Technical': 'Technical outerwear',
  'Warm Clothing': 'Warm',
  'Winter Must Haves': 'Winter',
  'Winter Essentials': 'Winter',
  "Women's Fashion": "Women's",
  'Junior': 'Kids',
  'Baby & Toddler': 'Baby & toddler',
  'Athleisurewear': 'Athleisure',
  'Activewear & Performance': 'Activewear',
  'Premium Sports': 'Sports',
  'Sports & Leisure': 'Sports',
  'Aprons & Service': 'Aprons',
  'Homewares & Towelling': 'Homewares & towels',
  'Gifting & Accessories': 'Gifts & accessories',
  'Shirts & Blouses': 'Shirts',
  'T-Shirts & Vests': 'T-Shirts',
  'Fashion Tees': 'T-Shirts',
  'Hoodies': 'Hoodies & Sweats',
  'Sweatshirts': 'Hoodies & Sweats',
  'Polos & Casual': 'Polos',
  'Organic & Conscious': 'Organic',
  'Outdoor Sports': 'Outdoor',
  'Personal Protection': 'Safetywear',
  'PPE': 'Safetywear',
  'Lounge & Underwear': 'Pyjamas & Underwear',
  'Lounge Sets': 'Loungewear',
  'Petwear & Accessories 2026': 'Petwear',
  'Summer Softshells': 'Softshells',
  'Summer Accessories': 'Accessories',
  'Checked shirts': 'Shirts',
  'Football Merchandise': 'Merch',
  'Polos safe to wash at 60 degrees': 'Wash at 60°',
  'Performance T-Shirts': 'T-Shirts',
};

// Who or what the garment is for.
const SECTOR = new Set([
  'Workwear', 'Hospitality', 'Safetywear', 'Sports', 'Activewear', 'Athleisure', 'Merch', 'Golf', 'Travel',
  'Festival', 'Events', 'Education', 'Corporate', 'Health & Beauty', 'Leisure', 'Loungewear', 'Team Kits',
  'Staffwear', 'Trade', 'Schoolwear', 'Uniform', 'Medical', 'Beauty', 'Fitness', 'Outdoor', 'Cycling', 'Running',
  'Chefswear', 'Crafting', 'Resortwear', 'Streetwear',
]);

// Properties of the garment, not a family or a sector.
const ATTRIBUTE = new Set([
  'Plus Sizes', 'Rebrandable', 'Oversized', 'Wash at 60°', 'UPF protection', 'Camo', 'Kids', 'Baby & toddler',
  "Women's", "Men's", 'Unisex', 'Sustainable', 'Recycled', 'Organic', 'Winter', 'Warm', 'Padded', 'Rainwear',
  'Technical outerwear', 'Waterproof', 'Hi Vis', 'Hi-Vis', 'Long Sleeve', 'Short Sleeve', 'Heavyweight',
  'Lightweight', 'Midweight', 'Fairtrade', 'Vegan', 'Fleece lined', 'Reflective', 'Stretch', 'Tall', 'Maternity',
  'Sublimation', 'Cropped', 'Denim', 'Longer Length',
]);

export interface ClassifiedTags {
  category: string[];
  sector: string[];
  attributes: string[];
  dropped: string[];
}

const normalise = (raw: string): string => {
  const t = raw.replace(/�/g, '').trim();
  return RENAME[t] ?? t;
};

export const classifyTags = (raw: string[]): ClassifiedTags => {
  const out: ClassifiedTags = { category: [], sector: [], attributes: [], dropped: [] };
  const seen = new Set<string>();
  for (const r of raw) {
    const t = r.replace(/�/g, '').trim();
    // Fragments: the feed cuts the tag string at 400 characters.
    if (t.length < 5) continue;
    if (DROP.some((re) => re.test(t))) {
      out.dropped.push(t);
      continue;
    }
    const name = normalise(t);
    if (seen.has(name)) continue;
    seen.add(name);
    if (SECTOR.has(name)) out.sector.push(name);
    else if (ATTRIBUTE.has(name)) out.attributes.push(name);
    else out.category.push(name);
  }
  return out;
};
